import React, { useEffect, useState, useMemo } from 'react';
import { Button, TextField, FormControl, InputLabel, Select as MuiSelect, MenuItem } from '@material-ui/core';
import { ResponsiveContainer, BarChart as RBarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import { httpClient } from '../utils/dataProvider';
import { PageHeader, Surface, MetricCard, Empty } from '../components/ui';

const BarChart = ({ data, height = 220, barColor = '#3f51b5' }) => {
    if (!data || !data.length) return <Empty>No data</Empty>;
    return (
        <div style={{ width: '100%', height }}>
            <ResponsiveContainer width="100%" height="100%">
                <RBarChart data={data} margin={{ top: 8, right: 12, bottom: 32, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-45} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip formatter={(v) => [v, 'Count']} />
                    <Bar dataKey="value" fill={barColor} radius={[2, 2, 0, 0]} />
                </RBarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default function PageAnalytics() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rangeType, setRangeType] = useState('LAST_30');
    const today = useMemo(() => new Date(), []);
    const [customFrom, setCustomFrom] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10));
    const [customTo, setCustomTo] = useState(today.toISOString().slice(0, 10));

    const calcRange = () => {
        const end = new Date();
        let start;
        switch (rangeType) {
            case 'LAST_7': start = new Date(); start.setDate(end.getDate() - 6); break;
            case 'LAST_30': start = new Date(); start.setDate(end.getDate() - 29); break;
            case 'THIS_MONTH': start = new Date(end.getFullYear(), end.getMonth(), 1); break;
            case 'CUSTOM':
                start = new Date(customFrom + 'T00:00:00Z');
                return { start, end: new Date(customTo + 'T23:59:59Z') };
            default: start = new Date(); start.setDate(end.getDate() - 29); break;
        }
        return { start, end };
    };

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const { start, end } = calcRange();
            const startParam = start.toISOString().slice(0, 10);
            const endParam = end.toISOString().slice(0, 10);
            // Single bounded request instead of a serial page-by-page loop.
            // Backend sorts createdAt desc and is indexed on {action,createdAt},
            // so limit=5000 returns the newest 5000 analytics events in one
            // round trip (same cap as the former 50×100 loop).
            const url = `https://skilzapp.net/api/logs?page=1&limit=5000&startdate=${startParam}&enddate=${endParam}&action=PAGE_VIEW,SESSION_END,APP_BACKGROUND,APP_FOREGROUND`;
            const { json } = await httpClient(url, { method: 'GET' });
            setLogs(json && Array.isArray(json.docs) ? json.docs : []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchLogs(); }, [rangeType, customFrom, customTo]);

    const pageViews = useMemo(() => logs.filter(l => l.action === 'PAGE_VIEW'), [logs]);
    const exitEvents = useMemo(() => logs.filter(l => l.action === 'APP_BACKGROUND' || l.action === 'SESSION_END'), [logs]);

    // ── Page Visit Rankings ──
    const pageRankings = useMemo(() => {
        const counts = {};
        const durations = {};
        const bounces = {};
        pageViews.forEach(l => {
            const p = l.page || 'unknown';
            counts[p] = (counts[p] || 0) + 1;
            if (l.duration != null) {
                durations[p] = durations[p] || [];
                durations[p].push(l.duration);
            }
            // A "bounce" is when previousPage is null (entry) and duration < 5s
            if (!l.previousPage && l.duration != null && l.duration < 5) {
                bounces[p] = (bounces[p] || 0) + 1;
            }
        });
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 15)
            .map(([page, count]) => ({
                page,
                count,
                avgDuration: durations[page]
                    ? (durations[page].reduce((a, b) => a + b, 0) / durations[page].length).toFixed(1)
                    : '—',
                bounceRate: bounces[page] ? `${((bounces[page] / count) * 100).toFixed(0)}%` : '0%',
            }));
    }, [pageViews]);

    const pageRankingChart = useMemo(() =>
        pageRankings.map(r => ({ label: r.page.length > 18 ? r.page.slice(0, 18) + '…' : r.page, value: r.count })),
        [pageRankings]);

    // ── Exit Pages ──
    const exitPages = useMemo(() => {
        const counts = {};
        exitEvents.forEach(l => {
            const p = l.page || 'unknown';
            counts[p] = (counts[p] || 0) + 1;
        });
        return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 15);
    }, [exitEvents]);

    const exitPageChart = useMemo(() =>
        exitPages.map(([page, count]) => ({ label: page.length > 18 ? page.slice(0, 18) + '…' : page, value: count })),
        [exitPages]);

    // ── Page Flow (transitions) ──
    const pageFlow = useMemo(() => {
        const counts = {};
        pageViews.forEach(l => {
            if (l.previousPage && l.page) {
                const key = `${l.previousPage} → ${l.page}`;
                counts[key] = (counts[key] || 0) + 1;
            }
        });
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 25)
            .map(([flow, count]) => {
                const [from, to] = flow.split(' → ');
                return { from, to, count };
            });
    }, [pageViews]);

    // ── Session stats ──
    const uniqueSessions = useMemo(() => new Set(logs.filter(l => l.sessionId).map(l => l.sessionId)).size, [logs]);
    const avgPageDuration = useMemo(() => {
        const withDuration = pageViews.filter(l => l.duration != null);
        if (!withDuration.length) return '—';
        return (withDuration.reduce((a, l) => a + l.duration, 0) / withDuration.length).toFixed(1) + 's';
    }, [pageViews]);

    return (
        <main className="sk-page">
            <PageHeader eyebrow="Insights" title="Page Analytics" description="Understand which pages users visit, where they leave, and how they navigate." />

            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                    <InputLabel id="pa-range">Range</InputLabel>
                    <MuiSelect labelId="pa-range" label="Range" value={rangeType} onChange={e => setRangeType(e.target.value)}>
                        <MenuItem value="THIS_MONTH">This Month</MenuItem>
                        <MenuItem value="LAST_7">Last 7 Days</MenuItem>
                        <MenuItem value="LAST_30">Last 30 Days</MenuItem>
                        <MenuItem value="CUSTOM">Custom</MenuItem>
                    </MuiSelect>
                </FormControl>
                {rangeType === 'CUSTOM' && <>
                    <TextField label="From" type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} InputLabelProps={{ shrink: true }} variant="outlined" size="small" />
                    <TextField label="To" type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} InputLabelProps={{ shrink: true }} variant="outlined" size="small" />
                </>}
            </div>

            {loading && <div className="sk-state is-loading" role="status">Loading page analytics…</div>}

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <MetricCard label="Page Views" value={pageViews.length} detail="Total PAGE_VIEW events" />
                <MetricCard tone="blue" label="Sessions" value={uniqueSessions} detail="Unique session IDs" />
                <MetricCard tone="purple" label="Avg Page Duration" value={avgPageDuration} detail="Mean time on page" />
                <MetricCard tone="orange" label="Exit Events" value={exitEvents.length} detail="APP_BACKGROUND + SESSION_END" />
            </div>

            <Surface title="Most Visited Pages" description="Top 15 pages by visit count">
                <BarChart data={pageRankingChart} barColor="#3f51b5" />
                {pageRankings.length > 0 && <table style={{ width: '100%', fontSize: 13, marginTop: 12, borderCollapse: 'collapse' }}>
                    <thead><tr style={{ borderBottom: '2px solid #eee' }}><th style={{ textAlign: 'left', padding: 6 }}>Page</th><th style={{ textAlign: 'right', padding: 6 }}>Visits</th><th style={{ textAlign: 'right', padding: 6 }}>Avg Duration</th><th style={{ textAlign: 'right', padding: 6 }}>Bounce Rate</th></tr></thead>
                    <tbody>{pageRankings.map(r => <tr key={r.page} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: 6 }}>{r.page}</td>
                        <td style={{ textAlign: 'right', padding: 6 }}>{r.count}</td>
                        <td style={{ textAlign: 'right', padding: 6 }}>{r.avgDuration}s</td>
                        <td style={{ textAlign: 'right', padding: 6 }}>{r.bounceRate}</td>
                    </tr>)}</tbody>
                </table>}
                {!pageRankings.length && <Empty>No page view data yet.</Empty>}
            </Surface>

            <Surface title="Exit Pages" description="Pages where users close or background the app">
                <BarChart data={exitPageChart} barColor="#e53935" />
                {exitPages.length > 0 && <table style={{ width: '100%', fontSize: 13, marginTop: 12, borderCollapse: 'collapse' }}>
                    <thead><tr style={{ borderBottom: '2px solid #eee' }}><th style={{ textAlign: 'left', padding: 6 }}>Page</th><th style={{ textAlign: 'right', padding: 6 }}>Exit Count</th><th style={{ textAlign: 'right', padding: 6 }}>% of Exits</th></tr></thead>
                    <tbody>{exitPages.map(([page, count]) => <tr key={page} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: 6 }}>{page}</td>
                        <td style={{ textAlign: 'right', padding: 6 }}>{count}</td>
                        <td style={{ textAlign: 'right', padding: 6 }}>{exitEvents.length ? ((count / exitEvents.length) * 100).toFixed(1) : 0}%</td>
                    </tr>)}</tbody>
                </table>}
                {!exitPages.length && <Empty>No exit data yet.</Empty>}
            </Surface>

            <Surface title="Page Flow" description="Top page-to-page transitions">
                {pageFlow.length > 0 && <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                    <thead><tr style={{ borderBottom: '2px solid #eee' }}><th style={{ textAlign: 'left', padding: 6 }}>From Page</th><th style={{ textAlign: 'center', padding: 6 }}>→</th><th style={{ textAlign: 'left', padding: 6 }}>To Page</th><th style={{ textAlign: 'right', padding: 6 }}>Count</th></tr></thead>
                    <tbody>{pageFlow.map((f, i) => <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: 6 }}>{f.from}</td>
                        <td style={{ textAlign: 'center', padding: 6, color: '#888' }}>→</td>
                        <td style={{ padding: 6 }}>{f.to}</td>
                        <td style={{ textAlign: 'right', padding: 6, fontWeight: 600 }}>{f.count}</td>
                    </tr>)}</tbody>
                </table>}
                {!pageFlow.length && <Empty>No navigation flow data yet.</Empty>}
            </Surface>
        </main>
    );
}
